import { getTenantSubdomain } from './api';

/**
 * Per-deployment instance identity surfaced in the UI. Values are injected at
 * build time via Vite env vars when an instance is provisioned. All are optional
 * so local development simply shows nothing.
 *
 * The shared Pages SPA is built for the demo. Tenant hostnames must not inherit
 * that baked demo identity.
 */
export interface FrontendInstanceInfo {
  id?: string;
  name?: string;
  environment?: string;
  version?: string;
}

export function getInstanceInfo(): FrontendInstanceInfo {
  const env = import.meta.env;
  const tenant = getTenantSubdomain();
  if (tenant) {
    return {
      name: tenant,
      environment: 'production',
      version: (env.VITE_APP_VERSION as string | undefined)?.trim() || undefined,
    };
  }
  return {
    id: (env.VITE_INSTANCE_ID as string | undefined)?.trim() || undefined,
    name: (env.VITE_INSTANCE_NAME as string | undefined)?.trim() || undefined,
    environment: (env.VITE_INSTANCE_ENV as string | undefined)?.trim() || undefined,
    version: (env.VITE_APP_VERSION as string | undefined)?.trim() || undefined,
  };
}

/** Short, display-friendly instance id (first segment of a UUID). */
export function shortInstanceId(id?: string): string | undefined {
  if (!id) return undefined;
  return id.length > 8 ? id.slice(0, 8) : id;
}

/** True when this build or hostname is the public demo / evaluator experience. */
export function isDemoHost(): boolean {
  if (getTenantSubdomain()) return false;
  const env = (getInstanceInfo().environment || '').toLowerCase();
  if (env === 'demo' || env === 'development' || env === 'dev') return true;
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.includes('pages.dev');
}
